import { Component, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, catchError, of, switchMap } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { HttpError } from 'src/app/core/http-error';
import { ParamsValues } from 'src/app/models/params-values';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { ParamsValuesService } from 'src/app/services/params-values.service';
import { ParametersValuesFormContext } from '../parameters/parameters-values-form/parameters-values-form-context';
import { ParametersValuesFormComponent } from '../parameters/parameters-values-form/parameters-values-form.component';

import { MatExpansionModule } from '@angular/material/expansion';

@Component({
    selector: 'app-organization-parameters',
    templateUrl: './organization-parameters.component.html',
    styleUrls: ['./organization-parameters.component.scss'],
    imports: [MatExpansionModule, ParametersValuesFormComponent],
})
export class OrganizationParametersComponent implements OnInit {
    public _paramsContext: ParametersValuesFormContext = null;

    constructor(
        private paramsValuesService: ParamsValuesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService,
        protected notificationService: NotificationService
    ) {}

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        this.load();
    }

    private load() {
        var context: ParametersValuesFormContext = new ParametersValuesFormContext();
        this.spinner.show('main-spinner');

        this.loadOrganizationParamsValues(this.memoryService.getSelectedOrganization().id)
            .pipe(
                catchError((error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de récupérer le paramétrage de l'organisation." + error.message);
                    throw error;
                }),
                switchMap((paramsValues) => {
                    context.formMode = FormMode.Edition;
                    context.formVisible = true;
                    context.paramsValues = paramsValues;

                    if (this.memoryService.getSelectedOrganization().organizationId) {
                        return this.loadOrganizationParamsValues(this.memoryService.getSelectedOrganization().organizationId).pipe(
                            catchError((error: HttpError) => {
                                this.notificationService.displayError('Erreur', "Impossible de récupérer le paramétrage de l'organisation parente." + error.message);
                                throw error;
                            })
                        );
                    } else {
                        context.groupParentParamsValues = null;
                        this._paramsContext = context;
                        this.spinner.hide('main-spinner');
                        return of(null);
                    }
                })
            )
            .subscribe({
                next: (parentparamsValues) => {
                    context.groupParentParamsValues = parentparamsValues;
                    this._paramsContext = context;
                    this.spinner.hide('main-spinner');
                },
                error: () => {
                    this.spinner.hide('main-spinner');
                },
            });
    }

    /**
     * Charge les données du jeu de paramètres de l'organisation.
     * @param organizationId : Id de l'organisation à charger.
     */
    private loadOrganizationParamsValues(organizationId: string): Observable<ParamsValues> {
        return this.paramsValuesService.getForOrganization(organizationId);
    }

    /**
     * Appelé lors de la mise à jour des paramètres.
     * @param paramsValues : Valeur du paramètre à mettre à jour.
     */
    public onParamsValuesUpdated(paramsValues: ParamsValues): void {
        this.spinner.show('main-spinner');
        if (paramsValues.id) {
            this.paramsValuesService.update(this.memoryService.getSelectedOrganization().id, paramsValues.id, paramsValues).subscribe(
                (x) => {
                    this._paramsContext.paramsValues = x;
                    this.notificationService.displaySuccess(null, "Paramètres de l'organisation mis à jour");
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de mettre à jour le paramétrage de l'organisation." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            this.paramsValuesService.createForOrganization(this.memoryService.getSelectedOrganization().id, paramsValues).subscribe(
                (x) => {
                    this._paramsContext.paramsValues = x;
                    this.notificationService.displaySuccess(null, "Paramètres de l'organisation créés");
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de créer le paramétrage de l'organisation." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque des changements sur le formulaire "infos" sont annulés.
     * @param user: Infos de l'article modifié.
     */
    public onCanceled(value: boolean) {
        this.load();
    }
}
